import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {LoginIds, RegisterRequirements, ResetPasswordMethod}
  from '../../main/modules/management/models/users-setting.model';
import {Authentication} from '../models/authentication.model';
import {Captcha} from '../models/captcha.model';
import {environment} from '../../../../environments/environment';
import {skip} from 'rxjs/operators';
import {VersionInfo} from '../models/version.model';
import {SupporterContactInfoModel} from '../models/supporter-contact-info.model';
import {Title} from "@angular/platform-browser";

@Injectable({
  providedIn: 'root',
})
export class AuthState {

  private userAuthentication$ = new BehaviorSubject<Partial<Authentication>>(null);
  private expireIn$ = new BehaviorSubject<any>(null);

  private isAuthenticated$ = new BehaviorSubject<boolean>(null);

  private loginIdsSettings$ = new BehaviorSubject<LoginIds[]>(null);

  private registerRequirements$ = new BehaviorSubject<RegisterRequirements[]>(null);

  private resetPasswordMethod$ = new BehaviorSubject<ResetPasswordMethod>(null);

  private registerByUser$ = new BehaviorSubject<boolean>(false);

  private supporterContactInfo$ = new BehaviorSubject<SupporterContactInfoModel>({
    email: '', phoneNumber: '',
  } as SupporterContactInfoModel);

  private versionInfo$ = new BehaviorSubject<VersionInfo>(null);

  private loading$ = new BehaviorSubject<boolean>(false);

  private captcha$ = new BehaviorSubject<Captcha>(null);

  private referenceId$ = new BehaviorSubject<string>(null);

  private phoneNumber$ = new BehaviorSubject<string>(null);

  private originalPhoneNumber$ = new BehaviorSubject<string>(null);

  private errorMessage$ = new BehaviorSubject<string>(null);

  private confirmationOTP$ = new BehaviorSubject<boolean>(null);

  private timerInSecond$ = new BehaviorSubject<number>(180);
  private generalSettings$ = new BehaviorSubject<Captcha>(null);
  private captchaRequired$ = new BehaviorSubject<boolean>(false);
  private surveyStatus$ = new BehaviorSubject<boolean>(false);
  private surveyDetails$ = new BehaviorSubject<any>(undefined);

  get expireIn(): any {
    return this.expireIn$.value;
  }

  constructor(private titleService: Title,){
  }

  updateUserAuthentication(userAuthentication: Partial<Authentication>) {
    this.userAuthentication$.next(userAuthentication);
  }

  getUserAuthentication$(): Observable<Partial<Authentication>> {
    return this.userAuthentication$.asObservable();
  }

  updateExpireIn(expireIn: any) {
    this.expireIn$.next(expireIn);
  }


  isAuthenticated(isAuthenticated: boolean): void {
    this.isAuthenticated$.next(isAuthenticated);
  }

  getAuthentication$(): Observable<any> {
    return this.isAuthenticated$.asObservable();
  }

  editLoading(loading: boolean): void {
    this.loading$.next(loading);
  }

  getLoading$(): Observable<any> {
    return this.loading$.asObservable();
  }

  setLoginIdsSettings(loginIdsSettings: any): void {
    const tempLoginIds = loginIdsSettings.split(', ');
    this.loginIdsSettings$.next(tempLoginIds);
  }

  getLoginIdsSettings$(): Observable<LoginIds[]> {
    return this.loginIdsSettings$.asObservable();
  }

  setRegisterRequirements(requirements: RegisterRequirements[]): void {
    this.registerRequirements$.next(requirements);
  }

  getRegisterRequirements$(): Observable<RegisterRequirements[]> {
    return this.registerRequirements$.asObservable();
  }

  setResetPasswordMethod(method: ResetPasswordMethod): void {
    this.resetPasswordMethod$.next(method);
  }

  getResetPasswordMethod$(): Observable<ResetPasswordMethod> {
    return this.resetPasswordMethod$.asObservable();
  }

  setRegisterByUser(canRegister: boolean): void {
    this.registerByUser$.next(canRegister);
  }

  setSupporterContactInfo(supporterContactInfo: SupporterContactInfoModel) {
    this.supporterContactInfo$.next(supporterContactInfo);
  }

  getSupporterContactInfo(): Observable<SupporterContactInfoModel> {
    return this.supporterContactInfo$.asObservable();
  }

  setGeneralSettings(generalSettings: any) {
    const {title} = generalSettings;
    this.titleService.setTitle(title);
    this.generalSettings$.next(generalSettings);
  }

  getGeneralSettings$(): Observable<any> {
    return this.generalSettings$.asObservable();
  }

  getVersionInfo$(): Observable<VersionInfo> {
    return this.versionInfo$.asObservable();
  }

  setVersionInfo(versionInfo: VersionInfo): void {
    this.versionInfo$.next(versionInfo);
  }

  getRegisterByUser$(): Observable<boolean> {
    return this.registerByUser$.asObservable();
  }

  updateCaptchaRequired(isRequired: boolean): void {
    this.captchaRequired$.next(isRequired);
  }

  getCaptchaRequired$(): Observable<boolean> {
    return this.captchaRequired$.asObservable();
  }

  updateCaptcha(shareKey: string): void {
    this.captcha$.next({
      shareKey,
      imagePath: `${environment.api}/Token/GetCaptchaImage?key=${shareKey}`,
    });
  }

  getCaptcha$(): Observable<Captcha> {
    return this.captcha$.asObservable().pipe(skip(1));
  }

  setReferenceId(referenceId: string): void {
    this.referenceId$.next(referenceId);
  }

  getReferenceId$(): Observable<string> {
    return this.referenceId$.asObservable();
  }

  setPhoneNumber(phoneNumber: string): void {
    this.phoneNumber$.next(phoneNumber);
  }

  getPhoneNumber$(): Observable<string> {
    return this.phoneNumber$.asObservable();
  }

  updateConfirmationOTP(confirmationOTP: boolean): void {
    this.confirmationOTP$.next(confirmationOTP);
  }

  getConfirmationOTP$(): Observable<boolean> {
    return this.confirmationOTP$.asObservable();
  }

  setOriginalPhoneNumber(phoneNumber: string) {
    this.originalPhoneNumber$.next(phoneNumber);
  }

  getOriginalPhoneNumber$(): Observable<string> {
    return this.originalPhoneNumber$.asObservable();
  }

  setErrorMessage(error: string): void {
    this.errorMessage$.next(error);
  }

  getErrorMessage$(): Observable<string> {
    return this.errorMessage$.asObservable();
  }

  setTimerInSecond(timerInSecond: number): void {
    this.timerInSecond$.next(timerInSecond);
  }

  getTimerInSecond$(): Observable<number> {
    return this.timerInSecond$.asObservable();
  }

  setSurveyStatus(isSurveyDone: boolean): void {
    this.surveyStatus$.next(isSurveyDone);
  }

  getSurveyStatus$(): Observable<boolean> {
    return this.surveyStatus$.asObservable();
  }

  setSurveyDetails(details:any): void {
    this.surveyDetails$.next(details);
  }

  getSurveyDetails$(): Observable<any> {
    return this.surveyDetails$.asObservable();
  }
}
